import { useEffect, useState } from 'react';
import { fetchMaterials, Material, downloadFile } from '../../../services/api';

function KnowledgePanelStyles() {
  return (
    <style>{`
      @keyframes panel-slide-in {
        from { transform: translateX(14px); opacity: 0; }
        to   { transform: translateX(0);    opacity: 1; }
      }
      .knowledge-panel {
        background: var(--ink-850);
        border-left: 1px solid var(--gilt-hair);
        animation: panel-slide-in 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
        position: relative; z-index: 1;
      }
      .knowledge-panel-header {
        background: transparent;
        border-bottom: 1px solid var(--gilt-hair);
      }
      .knowledge-eyebrow {
        font-family: var(--font-mono);
        font-size: 0.66rem; font-weight: 500;
        letter-spacing: 0.16em; text-transform: uppercase;
        color: var(--gilt);
      }
      .knowledge-count {
        font-family: var(--font-mono);
        font-size: 0.68rem; color: var(--vellum-faint);
        font-variant-numeric: tabular-nums;
      }
      .knowledge-intro {
        font-family: var(--font-read);
        font-size: 0.86rem; font-style: italic;
        color: var(--vellum-dim); line-height: 1.55; margin-bottom: 6px;
      }

      /* Entradas de recurso: filas de aparato, sin cajas */
      @keyframes res-rise {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .resource-row {
        position: relative;
        display: grid;
        grid-template-columns: 34px 1fr 18px;
        align-items: center;
        gap: 12px;
        width: 100%;
        text-align: left;
        background: none;
        border: none;
        border-bottom: 1px solid var(--gilt-hair);
        padding: 14px 6px 14px 12px;
        cursor: pointer;
        animation: res-rise 0.32s cubic-bezier(0.16,1,0.3,1) both;
        transition: background 0.2s ease, padding-left 0.24s cubic-bezier(0.16,1,0.3,1);
      }
      .resource-row::before {
        content: '';
        position: absolute; left: 0; top: 22%; bottom: 22%;
        width: 2px; background: var(--gilt);
        transform: scaleY(0); transform-origin: center;
        transition: transform 0.24s cubic-bezier(0.16,1,0.3,1);
      }
      .resource-row:hover:not(:disabled) {
        background: linear-gradient(90deg, var(--gilt-dim), transparent 70%);
        padding-left: 16px;
      }
      .resource-row:hover:not(:disabled)::before { transform: scaleY(1); }
      .resource-row:active:not(:disabled) { background: var(--gilt-dim); }
      .resource-row:disabled { opacity: 0.45; cursor: default; }
      .resource-glyph {
        width: 34px; height: 34px;
        display: flex; align-items: center; justify-content: center;
        border: 1px solid var(--gilt-hair);
        border-radius: 8px;
        background: var(--ink-800);
        color: var(--gilt);
        font-size: 1.05rem;
        transition: border-color 0.2s ease;
      }
      .resource-row:hover:not(:disabled) .resource-glyph { border-color: var(--gilt-line); }
      .resource-title {
        font-family: var(--font-ui);
        font-size: 0.84rem; font-weight: 500;
        color: var(--vellum); line-height: 1.3; margin-bottom: 3px;
      }
      .resource-type-badge {
        font-family: var(--font-mono);
        color: var(--vellum-faint);
        font-size: 0.62rem;
        letter-spacing: 0.1em;
        text-transform: uppercase;
      }
      .resource-dl { color: var(--gilt); opacity: 0.45; transition: opacity 0.2s ease; }
      .resource-row:hover:not(:disabled) .resource-dl { opacity: 1; }
    `}</style>
  );
}

interface Resource {
  id: string;
  title: string;
  type: string;
  icon: string;
  color: string;
  url?: string;
}

interface KnowledgePanelProps {
  subject: string;
}

/**
 * Detecta el tipo de archivo basándose en la extensión y el tipo proporcionado.
 * Devuelve el icono y color correspondientes.
 */
function getFileIconAndColor(
  fileName: string | undefined,
  fileType: string | undefined,
  url: string | undefined
): { icon: string; color: string } {
  // Extraer extensión del nombre del archivo o de la URL
  const getExtension = (): string | null => {
    const source = fileName || url || '';
    const match = source.match(/\.([a-z0-9]+)(?:[?#]|$)/i);
    return match ? match[1].toLowerCase() : null;
  };

  const extension = getExtension();
  const typeLower = (fileType || '').toLowerCase();
  const fileNameLower = (fileName || '').toLowerCase();

  // Detectar por extensión primero (más confiable)
  if (extension) {
    switch (extension) {
      case 'pdf':
        return {
          icon: 'ri-file-pdf-line',
          color: 'from-[#FF6633] to-[#e55a2b]',
        };
      case 'md':
      case 'markdown':
        return {
          icon: 'ri-markdown-line',
          color: 'from-[#1E5F8A] to-[#3A9ED4]',
        };
      case 'doc':
      case 'docx':
        return {
          icon: 'ri-file-word-line',
          color: 'from-[#2B579A] to-[#1e3d6b]',
        };
      case 'xls':
      case 'xlsx':
        return {
          icon: 'ri-file-excel-line',
          color: 'from-[#217346] to-[#185532]',
        };
      case 'ppt':
      case 'pptx':
        return {
          icon: 'ri-file-ppt-line',
          color: 'from-[#D04423] to-[#b8381d]',
        };
      case 'txt':
        return {
          icon: 'ri-file-text-line',
          color: 'from-[#6B7280] to-[#4B5563]',
        };
      case 'zip':
      case 'rar':
      case '7z':
        return {
          icon: 'ri-file-zip-line',
          color: 'from-[#8B5CF6] to-[#6D28D9]',
        };
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
      case 'webp':
        return {
          icon: 'ri-image-line',
          color: 'from-[#10B981] to-[#059669]',
        };
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
        return {
          icon: 'ri-video-line',
          color: 'from-[#EF4444] to-[#DC2626]',
        };
      case 'mp3':
      case 'wav':
      case 'ogg':
        return {
          icon: 'ri-music-line',
          color: 'from-[#F59E0B] to-[#D97706]',
        };
    }
  }

  // Detectar por tipo o nombre si no hay extensión
  if (typeLower.includes('pdf') || fileNameLower.includes('.pdf')) {
    return {
      icon: 'ri-file-pdf-line',
      color: 'from-[#FF6633] to-[#e55a2b]',
    };
  }

  if (
    typeLower.includes('markdown') ||
    typeLower.includes('md') ||
    fileNameLower.includes('.md') ||
    fileNameLower.includes('.markdown')
  ) {
    return {
      icon: 'ri-markdown-line',
      color: 'from-[#1E5F8A] to-[#3A9ED4]',
    };
  }

  if (
    typeLower.includes('artículo') ||
    typeLower.includes('articulo') ||
    typeLower.includes('article')
  ) {
    return {
      icon: 'ri-article-line',
      color: 'from-[#8B6914] to-[#C9A854]',
    };
  }

  if (
    typeLower.includes('lectura') ||
    typeLower.includes('libro') ||
    typeLower.includes('book')
  ) {
    return {
      icon: 'ri-book-open-line',
      color: 'from-[#8B6914] to-[#C9A854]',
    };
  }

  // Fallback genérico
  return {
    icon: 'ri-file-text-line',
    color: 'from-[#1E5F8A] to-[#3A9ED4]',
  };
}

export default function KnowledgePanel({ subject }: KnowledgePanelProps) {
  // Empezamos sin recursos por defecto; solo se mostrarán los que vengan de la API
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const handleDownload = async (url: string | undefined, fileName: string) => {
    if (!url) {
      setError('No hay URL disponible para descargar');
      return;
    }

    setDownloading(url);
    try {
      await downloadFile(url, fileName);
    } catch (err) {
      console.error('Error al descargar archivo:', err);
      setError(`Error al descargar el archivo: ${err instanceof Error ? err.message : 'Error desconocido'}`);
      // Limpiar el error después de 5 segundos
      setTimeout(() => setError(null), 5000);
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadMaterials = async () => {
      setLoading(true);
      setError(null);

      try {
        const apiMaterials: Material[] = await fetchMaterials(subject);

        const mapped: Resource[] = apiMaterials.map((m, index) => {
          const title = m.name || `Recurso ${index + 1}`;
          const type = m.type || 'Recurso';
          const url = m.url || m.path;

          // Detectar icono y color basándose en el tipo de archivo
          const { icon, color } = getFileIconAndColor(title, type, url);

          return {
            id: `${title}-${index}`,
            title,
            type,
            icon,
            color,
            url,
          };
        });

        if (!cancelled && mapped.length > 0) {
          setResources(mapped);
        } else if (!cancelled) {
          // Si la API no devuelve nada, dejamos la lista vacía
          setResources([]);
        }
      } catch (e) {
        console.error('Error cargando materiales desde la API', e);
        if (!cancelled) {
          setError(
            'No se han podido cargar los materiales en tiempo real.'
          );
          // En caso de error, no mostramos materiales por defecto
          setResources([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadMaterials();

    return () => {
      cancelled = true;
    };
  }, [subject]);

  return (
    <>
      <KnowledgePanelStyles />
      <div className="knowledge-panel w-80 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="knowledge-panel-header px-5 py-4 flex items-center justify-between">
          <span className="knowledge-eyebrow">Recursos</span>
          {resources.length > 0 && (
            <span className="knowledge-count">{String(resources.length).padStart(2, '0')}</span>
          )}
        </div>

        {/* Resources List */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <p className="knowledge-intro">
            Materiales de {subject}
          </p>

          {loading && (
            <div className="tool-loading" style={{ padding: '10px 0' }}>
              <i className="ri-loader-4-line animate-spin" />
              <span>Cargando materiales…</span>
            </div>
          )}

          {error && (
            <div style={{ background: 'rgba(201,123,110,0.1)', border: '1px solid rgba(201,123,110,0.35)', borderLeft: '2px solid var(--rose)', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px' }}>
              <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.78rem', color: 'var(--rose)', lineHeight: 1.5 }}>{error}</p>
            </div>
          )}

          {resources.length === 0 && !loading && !error && (
            <div className="text-center py-10">
              <div style={{
                width: '52px',
                height: '52px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--gilt-line)',
                background: 'var(--ink-800)',
                borderRadius: '13px',
                margin: '0 auto 14px',
              }}>
                <i className="ri-inbox-line text-2xl" style={{ color: 'var(--gilt)' }}></i>
              </div>
              <p style={{ fontFamily: 'var(--font-read)', fontStyle: 'italic', fontSize: '0.9rem', color: 'var(--vellum-dim)', lineHeight: 1.6 }}>
                Aún no hay materiales disponibles.
              </p>
            </div>
          )}

          {resources.length > 0 && (
            <div>
              {resources.map((resource, index) => (
                <button
                  key={resource.id}
                  onClick={() => handleDownload(resource.url, resource.title)}
                  disabled={!resource.url || downloading === resource.url}
                  className="resource-row"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <span className="resource-glyph">
                    <i className={resource.icon}></i>
                  </span>
                  <span className="min-w-0">
                    <span className="resource-title line-clamp-2" style={{ display: 'block' }}>
                      {resource.title}
                    </span>
                    <span className="resource-type-badge">{resource.type}</span>
                  </span>
                  {downloading === resource.url ? (
                    <i className="ri-loader-4-line animate-spin resource-dl" style={{ opacity: 1 }}></i>
                  ) : (
                    <i className="ri-download-line resource-dl"></i>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
